import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import {
  type EdgeDirection,
  type EdgeId,
  type EdgeLabels,
  type NodeSn,
  type PacketHeader,
  type PathLookupGraphPreset,
  type ReportSeverity,
  type TrafficScore,
  headerTypes,
} from '@ipfabric/utils';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { useLocale } from 'containers/App/LocaleContext';
import { type FC, type ReactNode, memo, useEffect, useState } from 'react';
import { isOneOf } from 'utils/isOneOf';
import type {
  DeviceViewElementType,
  PathLookupPacketDiff,
} from '../../deviceViewTypes';
import { PacketDetailBody } from './PacketDetailBody';
import {
  PacketDetailCloseButton,
  type PacketDetailCloseEvent,
} from './PacketDetailCloseButton';
import { PacketDetailLabels } from './PacketDetailLabels';
import { PacketDetailPlaceholder } from './PacketDetailPlaceholder';
import { PacketDetailSeverityInfo } from './PacketDetailSeverityInfo';
import { PacketDetailTrafficScore } from './PacketDetailTrafficScore';

export interface PacketDetailMouseEvent {
  detailId: EdgeId;
  deviceViewEntityNodeSn?: NodeSn;
  deviceViewElement: DeviceViewElementType;
}

export interface PacketDetailNextButtonClickEvent {
  nodeSn: NodeSn;
  edgeId: EdgeId;
}

export interface PacketDetailPreviousButtonClickEvent {
  nodeSn: NodeSn;
  edgeId: EdgeId;
}

type EdgeDiffType = 'removed' | 'added';

const hasPacketHeaderDetail = (packetHeader: PacketHeader): boolean => {
  if (!isOneOf(packetHeader.type, ...headerTypes)) {
    // Avoid rendering detail of unknown packets in case we forgot
    // to update supported packets in graph-utils
    stumble('Unknown packet header');
    return false;
  }
  // Currently all supported packets have some detail.
  // CAPWAP is allowed to be empty.
  return true;
};

interface PacketDetailProps {
  detailId: EdgeId;
  deviceViewElement: DeviceViewElementType;
  deviceViewEntityNodeSn?: NodeSn;
  diff?: PathLookupPacketDiff;
  disableHover: boolean;
  edgeDiffType?: EdgeDiffType;
  edgeDirection?: EdgeDirection;
  hasNextPathLookupNode?: boolean;
  hasPreviousPathLookupNode?: boolean;
  labels?: EdgeLabels;
  onHidePacketDetailAnimation?: (event: { detailId: EdgeId }) => void;
  onNextButtonClick?: (event: PacketDetailNextButtonClickEvent) => void;
  onNextButtonMouseEnter?: (event: PacketDetailMouseEvent) => void;
  onNextButtonMouseLeave?: (event: PacketDetailMouseEvent) => void;
  onPacketDetailClose?: (event: PacketDetailCloseEvent) => void;
  onPacketDetailDestroy?: (event: PacketDetailMouseEvent) => void;
  onPacketDetailHover?: (event: PacketDetailMouseEvent) => void;
  onPacketDetailLeave?: (event: PacketDetailMouseEvent) => void;
  onPreviousButtonClick?: (event: PacketDetailPreviousButtonClickEvent) => void;
  onPreviousButtonMouseEnter?: (event: PacketDetailMouseEvent) => void;
  onPreviousButtonMouseLeave?: (event: PacketDetailMouseEvent) => void;
  packet: (PacketHeader | null)[];
  pathLookupPreset: PathLookupGraphPreset;
  severityInfo?: Record<ReportSeverity, number>;
  showAnimation: boolean;
  showEmptyPlaceholder: boolean;
  sourceLabel: string | null;
  targetLabel: string | null;
  title?: ReactNode;
  trafficScore?: TrafficScore;
}

const BarePacketDetail: FC<PacketDetailProps> = (props) => {
  const [contentHover, setContentHover] = useState<boolean>(false);
  const { detailId } = props;
  const msg = useLocale();

  const handlePacketDetailMouseEnter = () => {
    if (contentHover) {
      return;
    }
    props.onPacketDetailHover?.({
      detailId,
      deviceViewElement: props.deviceViewElement,
    });
    setContentHover(true);
  };

  const handlePacketDetailMouseLeave = () => {
    props.onPacketDetailLeave?.({
      detailId,
      deviceViewElement: props.deviceViewElement,
    });
    setContentHover(false);
  };

  const handleNextButtonClick = () => {
    if (props.deviceViewEntityNodeSn !== undefined) {
      props.onNextButtonClick?.({
        nodeSn: props.deviceViewEntityNodeSn,
        edgeId: props.detailId,
      });
    }
  };

  const handleNextButtonMouseEnter = () => {
    props.onNextButtonMouseEnter?.({
      detailId,
      deviceViewElement: props.deviceViewElement,
      deviceViewEntityNodeSn: props.deviceViewEntityNodeSn,
    });
  };

  const handleNextButtonMouseLeave = () => {
    props.onNextButtonMouseLeave?.({
      detailId,
      deviceViewElement: props.deviceViewElement,
      deviceViewEntityNodeSn: props.deviceViewEntityNodeSn,
    });
  };

  const handlePreviousButtonClick = () => {
    if (props.deviceViewEntityNodeSn !== undefined) {
      props.onPreviousButtonClick?.({
        nodeSn: props.deviceViewEntityNodeSn,
        edgeId: props.detailId,
      });
    }
  };

  const handlePreviousButtonMouseEnter = () => {
    props.onPreviousButtonMouseEnter?.({
      detailId,
      deviceViewElement: props.deviceViewElement,
      deviceViewEntityNodeSn: props.deviceViewEntityNodeSn,
    });
  };

  const handlePreviousButtonMouseLeave = () => {
    props.onPreviousButtonMouseLeave?.({
      detailId,
      deviceViewElement: props.deviceViewElement,
      deviceViewEntityNodeSn: props.deviceViewEntityNodeSn,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: do once on mount
  useEffect(() => {
    return () => {
      if (props.onPacketDetailClose) {
        props.onPacketDetailDestroy?.({
          detailId,
          deviceViewElement: props.deviceViewElement,
        });
      }
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: do only on showAnimation
  useEffect(() => {
    const hideAnimation = async () => {
      await sleep(3000);
      props.onHidePacketDetailAnimation?.({ detailId });
    };
    if (props.showAnimation) {
      hideAnimation();
    }
  }, [props.showAnimation]);

  return (
    <div
      className={classNames(
        props.showAnimation && 'device-view-packet-detail--shadow-blink',
        !props.disableHover &&
          contentHover &&
          !props.showAnimation &&
          'device-view-packet-detail--shadow',
        'device-view-packet-detail',
        'flex',
        'flex-col',
        'shrink-0',
        'rounded-none',
        'mb-0',
        'h-full',
      )}
      onMouseEnter={handlePacketDetailMouseEnter}
      onMouseLeave={handlePacketDetailMouseLeave}
    >
      {props.title === undefined && props.labels && props.edgeDirection && (
        <PacketDetailLabels
          detailId={detailId}
          edgeDirection={props.edgeDirection}
          labels={props.labels}
          onPathLookupDetailClose={props.onPacketDetailClose}
          sourceLabel={props.sourceLabel}
          targetLabel={props.targetLabel}
        />
      )}
      {props.title !== undefined && (
        <div className='flex w-full items-center p-5'>
          <div className='flex-1 font-bold'>{props.title}</div>
          {props.onPacketDetailClose !== undefined && (
            <PacketDetailCloseButton
              detailId={props.detailId}
              onPathLookupDetailClose={props.onPacketDetailClose}
            />
          )}
        </div>
      )}
      {props.onNextButtonClick !== undefined && props.hasNextPathLookupNode && (
        <Button
          className='mb-4 mr-5 self-end'
          onClick={handleNextButtonClick}
          onMouseEnter={handleNextButtonMouseEnter}
          onMouseLeave={handleNextButtonMouseLeave}
          variant='secondary'
          type='button'
        >
          {msg.deviceView.pathLookup.btnNext}
          <i className='fa fa-chevron-right ml-2' />
        </Button>
      )}
      {props.onPreviousButtonClick !== undefined &&
        props.hasPreviousPathLookupNode && (
          <Button
            className='mb-4 ml-5 self-start'
            onClick={handlePreviousButtonClick}
            onMouseEnter={handlePreviousButtonMouseEnter}
            onMouseLeave={handlePreviousButtonMouseLeave}
            variant='secondary'
            type='button'
          >
            <i className='fa fa-chevron-left mr-2' />
            {msg.deviceView.pathLookup.btnPrevious}
          </Button>
        )}
      {props.trafficScore !== undefined && (
        <PacketDetailTrafficScore trafficScore={props.trafficScore} />
      )}
      {props.severityInfo !== undefined && (
        <PacketDetailSeverityInfo
          severityInfo={props.severityInfo}
          wrapperClassName='mx-5 pb-5'
        />
      )}
      {props.showEmptyPlaceholder && <PacketDetailPlaceholder />}
      {props.packet.map(
        (packetHeader: PacketHeader | null, packetIndex: number) =>
          packetHeader === null ||
          !hasPacketHeaderDetail(packetHeader) ? null : (
            <PacketDetailBody
              diff={props.diff}
              edgeDiffType={props.edgeDiffType}
              // biome-ignore lint/suspicious/noArrayIndexKey: no better key
              key={packetIndex}
              packetHeader={packetHeader}
              packetIndex={packetIndex}
              pathLookupPreset={props.pathLookupPreset}
            />
          ),
      )}
      {props.showAnimation && (
        <div className='sticky bottom-0 flex flex-1 flex-col justify-end'>
          <div className='device-view-packet-detail__highlight-panel' />
        </div>
      )}
      <div className='z-elevated pointer-events-none sticky bottom-0 flex flex-1 flex-col justify-end'>
        <div
          className={classNames(
            'path-inspector-packet-select__highlight-border',
            contentHover &&
              'path-inspector-packet-select__highlight-border--hover',
          )}
        />
      </div>
    </div>
  );
};

export const PacketDetail = memo(BarePacketDetail);
